import { NgTemplateOutlet } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import { Component, DestroyRef, OnInit, computed, inject, signal, ChangeDetectionStrategy } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControl, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { ActivatedRoute, RouterLink } from '@angular/router';
import { from, of } from 'rxjs';
import { catchError, debounceTime, distinctUntilChanged, map, switchMap } from 'rxjs/operators';
import { AuthService } from '../../../core/auth/auth';
import {
  Competition,
  CompetitionsService,
  LeaderboardAward,
  Pick,
  awardValue,
  awardsForPick as findAwardsForPick,
} from '../../../core/competitions/competitions';
import { PeopleService, PersonSearchResult, ageFor } from '../../../core/people/people';
import type { PickPerson } from '../../../core/competitions/competitions';

/** F-005 — as-you-type search against the local catalog only: wait for
 *  typing to settle, and require enough characters that the search is
 *  actually meaningful, before hitting the API at all. F-029 — these two
 *  values are shared by convention across every celebrity search UI
 *  (this page, admin-people-list.ts, entries-modal.ts) — keep them in
 *  sync if you change one. */
const LIVE_SEARCH_DEBOUNCE_MS = 200;
const LIVE_SEARCH_MIN_LENGTH = 4;

@Component({
  selector: 'app-competition-picks',
  imports: [ReactiveFormsModule, RouterLink, NgTemplateOutlet],
  templateUrl: './competition-picks.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './competition-picks.css',
})
export class CompetitionPicks implements OnInit {
  private readonly route = inject(ActivatedRoute);
  private readonly competitions = inject(CompetitionsService);
  private readonly people = inject(PeopleService);
  private readonly auth = inject(AuthService);
  private readonly destroyRef = inject(DestroyRef);

  competitionId!: number;

  readonly loading = signal(true);
  readonly awardValue = awardValue;
  readonly errorMessage = signal<string | null>(null);
  readonly competition = signal<Competition | null>(null);
  readonly picks = signal<Pick[]>([]);
  /** Only fetched once past the submission cutoff (Slice 11) — this
   *  contestant's own leaderboard row, so awards can be matched to picks. */
  readonly myAwards = signal<LeaderboardAward[]>([]);
  readonly myTotalPoints = signal<number | null>(null);

  readonly searchForm = new FormGroup({
    q: new FormControl('', { nonNullable: true }),
  });
  readonly searching = signal(false);
  readonly searchError = signal<string | null>(null);
  readonly searchResults = signal<PersonSearchResult[]>([]);
  /** F-010 — only set inside the explicit search() below, never the live
   *  debounced typeahead (which already silently shows nothing below 3
   *  characters or on zero matches, by design). Reset to false on every
   *  live-search emission too, so it never persists once the user keeps
   *  typing past a "no results" explicit search. */
  readonly noResultsFound = signal(false);
  /** Reveals hiddenResults() below — reset on every new search so it never
   *  leaks stale state from a previous query. */
  readonly showHidden = signal(false);
  /** F-031 — true once the current query's results already reflect a
   *  Wikipedia check (either the catalog had zero hits, so search() got
   *  the automatic fallback, or the user explicitly asked via "Search
   *  Wikipedia anyway"). Gates that button so it only shows when a local
   *  match exists but Wikipedia hasn't been checked yet for this query. */
  readonly wikipediaAlsoSearched = signal(false);
  /** Tracks which specific (result, bucket) add is in flight, so only that button disables. */
  readonly addingKey = signal<string | null>(null);
  /** The most recently added pick's id — drives the gold flash on its row
   *  (see competition-picks.css) so it's obvious a pick landed once the
   *  search overlay above it closes. Cleared once the flash finishes. */
  readonly newlyAddedPickId = signal<number | null>(null);
  /** Tracks which pick's Death Check is in flight, so only that button disables. */
  readonly deathChecking = signal<number | null>(null);
  /** Transient inline notice for a Death Check result/failure — separate
   *  from errorMessage(), which gates the whole page's content. */
  readonly deathCheckNotice = signal<string | null>(null);
  /** A pick just discovered dead via Death Check — same gold flash as
   *  newlyAddedPickId, but a distinct moment (not "just added"). */
  readonly justDiedPickId = signal<number | null>(null);

  readonly regularPicks = computed(() => this.picks().filter((p) => !p.isAlternate));
  readonly alternatePicks = computed(() => this.picks().filter((p) => p.isAlternate));
  /** Merged list for the combined picks section — regular first, alternates
   *  always last, without a separate sort (the split is already binary). */
  readonly sortedPicks = computed(() => [...this.regularPicks(), ...this.alternatePicks()]);

  /**
   * Filters non-person noise out of the explicit Wikipedia search fallback
   * (e.g. a celebrity's movies/discography showing up as separate
   * candidates) — the backend now only resolves dateOfBirth for a
   * genuine day-precision Wikidata claim (see wikipedia.service.ts), so
   * "no birth date" reliably means "not a person article" there. Local
   * catalog hits are already-vetted real people and are never hidden
   * regardless of dateOfBirth.
   */
  readonly visibleResults = computed(() =>
    this.searchResults().filter((r) => r.source === 'local' || r.dateOfBirth !== null),
  );
  readonly hiddenResults = computed(() =>
    this.searchResults().filter((r) => r.source === 'wikipedia' && r.dateOfBirth === null),
  );

  readonly regularCapReached = computed(() => {
    const c = this.competition();
    return !c || this.regularPicks().length >= c.maxEntries;
  });
  readonly alternateCapReached = computed(() => {
    const c = this.competition();
    return !c || this.alternatePicks().length >= c.maxAlternates;
  });
  readonly pastCutoff = computed(() => {
    const c = this.competition();
    return !c || new Date() > new Date(c.submissionEndDate);
  });

  async ngOnInit(): Promise<void> {
    this.competitionId = Number(this.route.snapshot.paramMap.get('id'));
    this.wireLiveLocalSearch();
    await this.load();
  }

  /**
   * F-005 — searches the local catalog only (never Wikipedia) as the user
   * types, once they've paused for 250ms and typed at least 3 characters.
   * Reuses the same `searchResults` signal/template the explicit Enter/
   * Search action (search() below) populates, so the same result-row
   * rendering and Add buttons apply to both — hitting Enter/Search still
   * runs the full local-then-Wikipedia-fallback search and overwrites
   * whatever the live search last showed.
   */
  private wireLiveLocalSearch(): void {
    this.searchForm.controls.q.valueChanges
      .pipe(
        debounceTime(LIVE_SEARCH_DEBOUNCE_MS),
        map((q) => q.trim()),
        distinctUntilChanged(),
        switchMap((q) => {
          if (q.length < LIVE_SEARCH_MIN_LENGTH) return of<PersonSearchResult[]>([]);
          this.searching.set(true);
          this.searchError.set(null);
          return from(this.people.search(q, 10, true)).pipe(
            catchError(() => {
              this.searchError.set('Search failed — try again in a moment.');
              return of<PersonSearchResult[]>([]);
            }),
          );
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((results) => {
        this.searchResults.set(results);
        this.showHidden.set(false);
        this.noResultsFound.set(false);
        this.wikipediaAlsoSearched.set(false);
        this.searching.set(false);
      });
  }

  /** Explicit Enter/Search action — full local-then-Wikipedia-fallback search.
   *  F-031 — pass includeWikipedia=true (via the "Search Wikipedia anyway"
   *  button) to also check Wikipedia even when the catalog already has
   *  hits that aren't the right person (e.g. "Donald Trump" only matching
   *  "Donald Trump Jr." locally). */
  async search(includeWikipedia = false): Promise<void> {
    const q = this.searchForm.getRawValue().q.trim();
    if (!q) return;

    this.searching.set(true);
    this.searchError.set(null);
    this.noResultsFound.set(false);
    try {
      const results = await this.people.search(q, 10, false, false, includeWikipedia);
      this.searchResults.set(results);
      this.showHidden.set(false);
      this.noResultsFound.set(results.length === 0);
      this.wikipediaAlsoSearched.set(
        includeWikipedia || !results.some((r) => r.source === 'local'),
      );
    } catch {
      this.searchError.set('Search failed — try again in a moment.');
    } finally {
      this.searching.set(false);
    }
  }

  resultKey(result: PersonSearchResult, isAlternate: boolean): string {
    return `${result.wikipediaUrl ?? result.name}-${isAlternate}`;
  }

  awardsForPick(pick: Pick): LeaderboardAward[] {
    return findAwardsForPick(this.myAwards(), pick.id);
  }

  async addPick(result: PersonSearchResult, isAlternate: boolean): Promise<void> {
    const key = this.resultKey(result, isAlternate);
    this.addingKey.set(key);
    this.searchError.set(null);
    try {
      const personId =
        result.id ??
        (
          await this.people.select({
            name: result.name,
            knownFor: result.knownFor ?? undefined,
            dateOfBirth: result.dateOfBirth ?? undefined,
            dateOfDeath: result.dateOfDeath ?? undefined,
            causeOfDeath: result.causeOfDeath ?? undefined,
            wikipediaUrl: result.wikipediaUrl ?? undefined,
            imageUrl: result.imageUrl ?? undefined,
            aliases: result.aliases,
          })
        ).id;

      const pick = await this.competitions.addPick(this.competitionId, {
        personId,
        isAlternate,
      });
      this.picks.update((current) => [...current, pick]);

      // Close the search overlay and light up the row it just landed in —
      // the flash is the confirmation, so no separate toast is needed.
      this.searchResults.set([]);
      this.searchForm.controls.q.setValue('');
      this.newlyAddedPickId.set(pick.id);
      setTimeout(() => {
        if (this.newlyAddedPickId() === pick.id) this.newlyAddedPickId.set(null);
      }, 1200);
    } catch (err) {
      this.searchError.set(this.addErrorMessage(err));
    } finally {
      this.addingKey.set(null);
    }
  }

  /** Mirrors the backend's ageAtDeath() (scoring.service.ts) semantics.
   *  Shared with the competition detail page's own-picks list (F-015). */
  ageFor(person: PickPerson): number | null {
    return ageFor(person);
  }

  /** On-demand live Wikipedia check — a stopgap ahead of more proactive
   *  automatic death detection. Only ever called for an ALIVE pick with a
   *  wikipediaUrl (see template's *ngIf-equivalent condition). */
  async requestDeathCheck(pick: Pick): Promise<void> {
    this.deathChecking.set(pick.id);
    this.deathCheckNotice.set(null);
    try {
      const { died, person } = await this.people.checkForDeath(pick.personId);
      if (died) {
        this.picks.update((current) =>
          current.map((p) => (p.id === pick.id ? { ...p, person } : p)),
        );
        this.justDiedPickId.set(pick.id);
        setTimeout(() => {
          if (this.justDiedPickId() === pick.id) this.justDiedPickId.set(null);
        }, 1200);
      } else {
        this.deathCheckNotice.set(`${pick.person.name} still shows as alive on Wikipedia.`);
        setTimeout(() => this.deathCheckNotice.set(null), 4000);
      }
    } catch {
      this.deathCheckNotice.set('Could not check right now — try again in a moment.');
      setTimeout(() => this.deathCheckNotice.set(null), 4000);
    } finally {
      this.deathChecking.set(null);
    }
  }

  async removePick(pick: Pick): Promise<void> {
    this.errorMessage.set(null);
    try {
      await this.competitions.removePick(this.competitionId, pick.id);
      this.picks.update((current) => current.filter((p) => p.id !== pick.id));
    } catch {
      this.errorMessage.set('Could not remove that pick — try again.');
    }
  }

  private async load(): Promise<void> {
    this.loading.set(true);
    this.errorMessage.set(null);
    try {
      const competition = await this.competitions.getOne(this.competitionId);
      this.competition.set(competition);
      const picks = await this.competitions.listPicks(this.competitionId);
      this.picks.set(picks);
      if (this.pastCutoff()) {
        const leaderboard = await this.competitions.getLeaderboard(this.competitionId);
        const myEntry = leaderboard.find((e) => e.userId === this.auth.user()?.id);
        this.myAwards.set(myEntry?.awards ?? []);
        this.myTotalPoints.set(myEntry?.totalPoints ?? null);
      }
    } catch {
      this.errorMessage.set(
        "Could not load this competition's picks — check the link or try again.",
      );
    } finally {
      this.loading.set(false);
    }
  }

  private addErrorMessage(err: unknown): string {
    if (err instanceof HttpErrorResponse) {
      if (err.status === 409) {
        return "You've already picked that person, or you're at the cap for that bucket.";
      }
      if (err.status === 403) {
        return 'The submission deadline has passed, or this competition is on hold.';
      }
      if (err.status === 404) {
        return 'That person could not be found — try searching again.';
      }
    }
    return 'Something went wrong — try again in a moment.';
  }
}
